import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Filter, Search, X } from 'lucide-react';
import type { Resource } from '@itr/shared';
import { HEALTH_SEVERITY, registeredHosts } from '@itr/shared';
import { api } from '../api/client';
import { useFetch } from './ui';

/**
 * One filter for every device list in the app — the network map, a site's
 * resource table, a router's discovered devices: narrow by type and by
 * status (several of each at once), and by a free-text query that matches a
 * name, an IP or the start of a MAC address. Client-side only; the lists it
 * filters are already loaded.
 */

/** What the filter looks at on one item. */
export interface DeviceFields {
  /** Type key, or null for "unidentified"; grouped into the type menu. */
  kind: string | null;
  /** Human label for the type menu. */
  kindLabel: string;
  /** Status key, or null when the item has none; grouped into the status menu. */
  status: string | null;
  /** Human label for the status menu. */
  statusLabel: string;
  /** Lower sorts earlier in the status menu — worst-first for health. */
  statusRank?: number;
  /** Name, hostname, vendor, model, addresses… matched as a substring. */
  text: Array<string | null | undefined>;
  /** MAC addresses, matched by prefix in any separator style. */
  macs: Array<string | null | undefined>;
}

/** One line in a filter menu: what it selects, how it reads, how many it covers. */
export interface FacetOption {
  key: string;
  label: string;
  count: number;
  /** Sort order within the menu; ties break on the label. */
  rank: number;
}

/** Key for "no type" / "no status" in a menu and in the selected set. */
const NONE = '';

const normaliseMac = (s: string) => s.replace(/[^0-9a-f]/gi, '').toUpperCase();
/** Hex pairs with optional `:`, `-` or `.` separators — "00:0c", "000C29", "00-0c-2". */
const looksLikeMac = (q: string) => /^[0-9a-f]{2}([:\-.]?[0-9a-f]{1,2})*[:\-.]?$/i.test(q);

/**
 * True when the item matches the query: a substring of any text field, or —
 * when the query reads as a MAC prefix — the start of any of its MACs.
 * Empty query matches everything.
 */
export function matchesDeviceQuery(query: string, fields: Pick<DeviceFields, 'text' | 'macs'>): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (fields.text.some((t) => t && t.toLowerCase().includes(q))) return true;
  if (!looksLikeMac(q)) return false;
  const prefix = normaliseMac(q);
  return fields.macs.some((mac) => mac && normaliseMac(mac).startsWith(prefix));
}

/** Counts each distinct key once per item and orders the menu. */
function buildOptions(entries: Array<{ key: string; label: string; rank: number }>): FacetOption[] {
  const byKey = new Map<string, FacetOption>();
  for (const entry of entries) {
    const existing = byKey.get(entry.key);
    if (existing) existing.count += 1;
    else byKey.set(entry.key, { ...entry, count: 1 });
  }
  return [...byKey.values()].sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label));
}

export interface DeviceFilter<T> {
  visible: T[];
  query: string;
  setQuery: (q: string) => void;
  kinds: Set<string>;
  setKinds: (kinds: Set<string>) => void;
  kindOptions: FacetOption[];
  statuses: Set<string>;
  setStatuses: (statuses: Set<string>) => void;
  statusOptions: FacetOption[];
  active: boolean;
  clear: () => void;
}

/**
 * Filter state plus the filtered list. `describe` is called per item per
 * render, so keep it cheap — these lists are a few hundred rows at most.
 */
export function useDeviceFilter<T>(items: T[], describe: (item: T) => DeviceFields): DeviceFilter<T> {
  const [query, setQuery] = useState('');
  const [kinds, setKinds] = useState<Set<string>>(() => new Set());
  const [statuses, setStatuses] = useState<Set<string>>(() => new Set());

  const described = useMemo(() => items.map((item) => ({ item, fields: describe(item) })), [items, describe]);

  // "Unidentified" sorts last among types; statuses keep the describer's own
  // ranking, which for a resource is worst health first.
  const kindOptions = useMemo(
    () =>
      buildOptions(
        described.map(({ fields }) => ({
          key: fields.kind ?? NONE,
          label: fields.kindLabel,
          rank: fields.kind ? 0 : 1,
        })),
      ),
    [described],
  );
  const statusOptions = useMemo(
    () =>
      buildOptions(
        described.map(({ fields }) => ({
          key: fields.status ?? NONE,
          label: fields.statusLabel,
          rank: fields.statusRank ?? 0,
        })),
      ),
    [described],
  );

  const active = query.trim() !== '' || kinds.size > 0 || statuses.size > 0;
  const visible = useMemo(() => {
    if (!active) return items;
    return described
      .filter(
        ({ fields }) =>
          (kinds.size === 0 || kinds.has(fields.kind ?? NONE)) &&
          (statuses.size === 0 || statuses.has(fields.status ?? NONE)) &&
          matchesDeviceQuery(query, fields),
      )
      .map(({ item }) => item);
  }, [active, described, items, kinds, statuses, query]);

  return {
    visible,
    query,
    setQuery,
    kinds,
    setKinds,
    kindOptions,
    statuses,
    setStatuses,
    statusOptions,
    active,
    clear: () => {
      setQuery('');
      setKinds(new Set());
      setStatuses(new Set());
    },
  };
}

interface ResourceKindMeta {
  kind: string;
  label: string;
}

/**
 * Describes a `Resource` for the filter: its kind (labelled from the plugin
 * manifests), its health as the status, its name and every address it's
 * known on, and its MACs. Health sorts worst first, so "Down" is the top
 * line of the status menu rather than buried under "Degraded".
 */
export function useResourceDescriber(): (r: Resource) => DeviceFields {
  const { data: kinds } = useFetch(() => api.get<ResourceKindMeta[]>('/api/resource-kinds'), []);
  const labels = useMemo(() => new Map((kinds ?? []).map((k) => [k.kind, k.label])), [kinds]);
  return useMemo(
    () => (r: Resource) => ({
      kind: r.kind,
      kindLabel: labels.get(r.kind) ?? r.kind,
      status: r.health,
      statusLabel: r.health.charAt(0).toUpperCase() + r.health.slice(1),
      statusRank: -(HEALTH_SEVERITY[r.health] ?? 0),
      text: [r.name, r.address, ...registeredHosts(r), ...r.secondaryAddresses.map((a) => a.value), ...r.tags],
      macs: r.macAddresses.map((m) => m.value),
    }),
    [labels],
  );
}

/**
 * The search box and the type and status menus side by side, with a Clear
 * button once any of them is in use. Drop it in a card below the header; it
 * wraps on narrow screens.
 */
export function DeviceFilterBar<T>({
  filter,
  total,
  placeholder = 'Name, IP or MAC prefix…',
  children,
}: {
  filter: DeviceFilter<T>;
  /** Size of the unfiltered list, for the "n of total" note. */
  total: number;
  placeholder?: string;
  /** Extra controls, placed after the built-in ones. */
  children?: React.ReactNode;
}) {
  return (
    <div className="device-filter">
      <div className="device-filter-search">
        <Search size={14} className="device-filter-search-icon" />
        <input
          className="input"
          value={filter.query}
          onChange={(e) => filter.setQuery(e.target.value)}
          placeholder={placeholder}
          aria-label="Filter by name, IP or MAC prefix"
          spellCheck={false}
        />
        {filter.query && (
          <button type="button" className="device-filter-search-clear" onClick={() => filter.setQuery('')} aria-label="Clear search">
            <X size={12} />
          </button>
        )}
      </div>
      <FacetMultiSelect noun="types" options={filter.kindOptions} value={filter.kinds} onChange={filter.setKinds} />
      <FacetMultiSelect noun="statuses" options={filter.statusOptions} value={filter.statuses} onChange={filter.setStatuses} />
      {children}
      {filter.active && (
        <>
          <span className="subtle" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            {filter.visible.length} of {total}
          </span>
          <button type="button" className="btn btn-sm" onClick={filter.clear}>
            <X size={12} /> Clear
          </button>
        </>
      )}
    </div>
  );
}

/**
 * A button that opens a checklist — types, statuses — where any number can be
 * ticked and nothing ticked means "all of them". Closes on an outside click
 * or Escape. `noun` is the plural used throughout its wording.
 */
export function FacetMultiSelect({
  noun,
  options,
  value,
  onChange,
}: {
  noun: string;
  options: FacetOption[];
  value: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const title = `${noun.charAt(0).toUpperCase()}${noun.slice(1)}`;

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Something ticked that has since vanished from the list (a rescan, a
  // delete, a device that changed state) still counts as a filter, so it is
  // named rather than silently dropped.
  const picked = [...value];
  const summary =
    picked.length === 0
      ? `All ${noun}`
      : picked.length === 1
        ? (options.find((o) => o.key === picked[0])?.label ?? (picked[0] === NONE ? 'None' : picked[0]))
        : `${picked.length} ${noun}`;

  const toggle = (key: string) => {
    const next = new Set(value);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  };

  return (
    <div className="kind-filter" ref={ref}>
      <button
        type="button"
        className={`btn btn-sm${value.size > 0 ? ' btn-primary' : ''}`}
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={`Show only some ${noun}`}
      >
        <Filter size={12} /> {summary} <ChevronDown size={12} />
      </button>
      {open && (
        <div className="kind-filter-menu" role="listbox" aria-multiselectable="true" aria-label={title}>
          {options.length === 0 && <div className="subtle kind-filter-empty">No {noun} to choose from</div>}
          {options.map((o) => (
            <label key={o.key} className="kind-filter-item" role="option" aria-selected={value.has(o.key)}>
              <input type="checkbox" checked={value.has(o.key)} onChange={() => toggle(o.key)} />
              <span className="grow truncate">{o.label}</span>
              <span className="subtle kind-filter-count">{o.count}</span>
            </label>
          ))}
          {value.size > 0 && (
            <button type="button" className="btn btn-sm kind-filter-clear" onClick={() => onChange(new Set())}>
              Show all {noun}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
